import { MomentModel, MomentStub } from '../../entities';
import { MomentSelector } from '../../entities/moments/momentSelector';
import { PlayerComponentName } from './playerSchema';

// ---------------------------------------------------------------------------
// Context interface — the subset of PlayerState needed by the moment helpers.
// Avoids a direct import of PlayerState and prevents circular imports.
// ---------------------------------------------------------------------------

export interface MomentsContext {
  readonly selector: MomentSelector | null;
  readonly isLiveStream: boolean;
  readonly job: { isDone: boolean; isMediaLevelDone?: boolean | null } | null;
  readonly momentEditor: { currentStub: MomentStub | null };
  readonly itemSource: { directorMoments: MomentModel[] };
  readonly playerLiveAdapter: { convertToRelativeTime(t: number): number };
  readonly adapter: { initPtsInSec: number };
  isComponentVisible(name: PlayerComponentName): boolean;
}

// ---------------------------------------------------------------------------
// Moment director / reporter enablement
// ---------------------------------------------------------------------------

export function isMomentDirectorEnabled(selector: MomentSelector | null): boolean {
  switch (selector?.outputMode) {
    case 'Topics':
    case 'Speakers':
      return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Show / hide logic
// ---------------------------------------------------------------------------

export function shouldShowAddClip(ctx: MomentsContext): boolean {
  if (!ctx.isComponentVisible(PlayerComponentName.MomentEditorGroup))
    return false;
  return (ctx.job?.isDone || ctx.job?.isMediaLevelDone) ?? false;
}

// ---------------------------------------------------------------------------
// Moment stub getter
// ---------------------------------------------------------------------------

export function getMomentStub(ctx: MomentsContext): MomentStub | null {
  return ctx.momentEditor.currentStub ?? null;
}

// ---------------------------------------------------------------------------
// Pure helper
// ---------------------------------------------------------------------------

export function momentHasBookmark(moment?: MomentModel | null): boolean {
  return moment?.hasBookmark ?? false;
}

// ---------------------------------------------------------------------------
// Jump-to time calculations
// ---------------------------------------------------------------------------

export function getJumpToMomentTime(
  ctx: MomentsContext,
  momentStartTime: number,
): number {
  return ctx.isLiveStream
    ? ctx.playerLiveAdapter.convertToRelativeTime(momentStartTime)
    : momentStartTime;
}

export function getJumpToCommentTime(
  ctx: MomentsContext,
  videoTime: number,
): number {
  return ctx.isLiveStream
    ? ctx.playerLiveAdapter.convertToRelativeTime(videoTime)
    : videoTime;
}

export function getJumpToReactionTime(
  ctx: MomentsContext,
  videoTime: number,
): number {
  return ctx.isLiveStream
    ? ctx.playerLiveAdapter.convertToRelativeTime(videoTime)
    : videoTime;
}

export function getJumpToCommentOrReactionTime(
  ctx: MomentsContext,
  target: { videoTime?: number | null; isLive?: boolean },
  liveCliprHLS: boolean | null | undefined,
): number {
  const videoTime = target.videoTime ?? 0;

  if (ctx.isLiveStream)
    return ctx.playerLiveAdapter.convertToRelativeTime(videoTime);

  if (!!target.isLive && !!liveCliprHLS)
    return videoTime - ctx.adapter.initPtsInSec;

  return videoTime;
}

// ---------------------------------------------------------------------------
// Initial seek helpers
// ---------------------------------------------------------------------------

export function getDirectorInitialSeekTime(
  directorMoments: MomentModel[],
): number | null {
  if (directorMoments.length > 0)
    return directorMoments[0].startTime;
  return null;
}
